'use client';
import {
  split, HttpLink, ApolloClient, InMemoryCache, gql, from,
} from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import * as is from 'is';
import { getLogger, hasFetch } from '@utils/general';
import { cache } from '../../App';

const log = getLogger('utils.graphql.client');

// GraphQL error handling @see: https://www.apollographql.com/docs/react/data/error-handling#on-graphql-errors
const retryLink = new RetryLink();
const errorLink = onError(({
  graphQLErrors, networkError, operation, forward,
}) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({
      message, locations, path,
    }) =>
      log.warn(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
    );
  }
  if (networkError) console.error(`[Network error]: ${networkError}`);
  return forward(operation);
});

export class Client {
  static #instance = null;

  #client = null;

  static singleton() {
    if (Client.#instance === null) {
      Client.#instance = new Client();
    }
    return Client.#instance;
  }

  constructor() { }

  get defaultValidLinks() {
    return [errorLink, retryLink];
  }

  get isSetup() {
    return this.#client !== null;
  }

  get client() {
    return this.#client;
  }

  setup(url) {
    log.info('setting up client url:', url);
    if (!is.string(url)) {
      log.warn('Non-string passed to setBaseUrl.');
      return;
    }
    if (is.empty(url)) {
      log.warn('Empty string passed to setBaseUrl.');
    }
    const wsUrl = `${url.replace(/^https?:\/\//, 'wss://')}/graphql`;
    log.info('websocket url:', wsUrl);
    const httpLink = this.#createHttpLink(url);
    const subscriptionLink = this.#createSubscriptionLink(wsUrl);
    const link = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      subscriptionLink,
      httpLink
    );
    const links = this.defaultValidLinks.concat(link);
    this.#client = new ApolloClient({ link: from(links), cache: new InMemoryCache() });
  }

  async query(name, query) {
    const response = await this.#client.query({
      query: gql(query),
      fetchPolicy: 'network-only',
      pollInterval: 0,
    });
    let error, loading = response.loading;
    if (response.errors?.length > 0) {
      error = response.errors.join(', ');
    } else if (response.error) {
      error = response.error;
    }

    const {
      success,
      error: queryError,
      data: queryData,
      pagination,
    } = response.data ? response.data[name] : {};

    if (!success) {
      error = queryError;
    }

    return {
      error,
      isLoading: loading,
      data: queryData,
      pagination,
    };
  }

  async queryWithoutSubfields(name, query) {
    let queryError, response;
    try {
      response = await this.#client.query({
        query: gql(query),
        fetchPolicy: 'network-only',
        pollInterval: 0,
      });
    }
    catch (error) {
      queryError = error;
    }
    let loading = response?.loading;

    return {
      error: queryError,
      isLoading: loading,
      data: response?.data[name],
    };
  }

  async mutate(name, mutation, bulkUpdate, streamInfo) {
    const response = await this.#client.mutate({
      mutation: gql(mutation),
      fetchPolicy: 'network-only',
      pollInterval: 0,
    });
    let error, loading = response.loading;
    if (response.errors?.length > 0) {
      error = response.errors.join(', ');
    } else if (response.error) {
      error = response.error;
    }

    const {
      success,
      error: queryError,
      data: queryData,
      tracking_id,
    } = response.data ? response.data[name] : {};

    if (!success) {
      error = queryError;
    } else {
      const regex = /online:\s*(true|false)/;
      const setOnlineOffline = regex.test(mutation);

      // add tracking id (channel) to cache only for setting a stream online/offline for now
      // this if statement will be taken out after GA and once the SFS messaging has been updated
      if (name === 'update_stream' && setOnlineOffline) {
        cache.set(tracking_id, {
          tracking_id: tracking_id,
          bulkUpdate: bulkUpdate,
          streamInfo: streamInfo,
        });
      }
    }

    return {
      error,
      isLoading: loading,
      data: queryData,
      tracking_id,
    };
  }

  //_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/
  // Private Methods
  //_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/_/

  #createHttpLink(url) {
    return hasFetch()
      ? new HttpLink({ uri: `${url}/graphql` })
      : null;
  }

  #createSubscriptionLink(url) {
    return new GraphQLWsLink(createClient({
      url,
      cache: new InMemoryCache(),
      shouldRetry: () => true,
      on: {
        connected() {
          log.info('WebSocket connection established');
        },
        closed() {
          log.info('WebSocket connection closed');
        },
        error(error) {
          log.error('WebSocket error:', error);
        },
      },
    }));
  }
}